import React, { useEffect, useState } from 'react';
import { useFirebaseAuth } from 'context/FirebaseAuthContext';
import { Alert, Badge, Card, Col, Row, Tab, Tabs } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import PageHeader from 'components/common/PageHeader';
import { useRateShopSettings } from './hooks/useRateShopSettings';
import { useRateShopRuns } from './hooks/useRateShopRuns';
import { useRateShopRules } from './hooks/useRateShopRules';
import { useRateShopDecisions } from './hooks/useRateShopDecisions';
import { useRateShopAccess } from './hooks/useRateShopAccess';
import { useShipStationStores } from './hooks/useShipStationStores';
import { modeFromSettings, MODE_OPTIONS } from './lib/settingsMode';
import { money } from './lib/describe';
import {
  ConfirmModal,
  Counts,
  dateTime,
  LoadError,
  Loading
} from './components/Common';
import RulesTab from './tabs/RulesTab';
import LogTab from './tabs/LogTab';
import RunsTab from './tabs/RunsTab';
import SettingsTab from './tabs/SettingsTab';

const TAB_KEYS = ['rules', 'log', 'runs', 'settings'];

const RateShop = () => {
  const [params, setParams] = useSearchParams();
  const requestedTab = TAB_KEYS.includes(params.get('tab'))
    ? params.get('tab')
    : 'rules';
  const settingsData = useRateShopSettings();
  const runsData = useRateShopRuns();
  const rulesData = useRateShopRules();
  const storesData = useShipStationStores();
  const [activeTab, setActiveTab] = useState(requestedTab);
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  const [user, authLoading] = useFirebaseAuth();
  const signedOut = !authLoading && !user;
  const decisionsData = useRateShopDecisions(undefined, activeTab === 'log');
  const { canEdit, loading: accessLoading, userError } = useRateShopAccess();
  const { settings } = settingsData;
  const { latest, savingsToday } = runsData;

  useEffect(() => {
    if (signedOut) {
      setSettingsDirty(false);
      setPendingTab(null);
    }
  }, [signedOut]);

  useEffect(() => {
    if (requestedTab === activeTab) return;
    if (activeTab === 'settings' && settingsDirty) {
      setPendingTab(requestedTab);
      const restored = new URLSearchParams(params);
      restored.set('tab', activeTab);
      setParams(restored, { replace: true });
    } else setActiveTab(requestedTab);
  }, [requestedTab, activeTab, settingsDirty, params, setParams]);
  useEffect(() => {
    if (!settingsDirty) return undefined;
    const leave = event => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', leave);
    return () => window.removeEventListener('beforeunload', leave);
  }, [settingsDirty]);
  const navigateTab = tab => {
    setSettingsDirty(false);
    setActiveTab(tab);
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next);
  };
  const signIn = <Alert variant="info">Sign in to view Rate Shop</Alert>;

  return (
    <>
      <PageHeader
        title="Rate Shop"
        description="Choose the cheapest comparable shipping service across package types. Review rules and audit every decision."
        className="mb-3"
      >
        {user && settings && (
          <div className="d-flex flex-wrap gap-2 mt-3">
            <Badge
              pill
              bg={
                !settings.enabled
                  ? 'danger'
                  : settings.dryRun
                  ? 'info'
                  : 'success'
              }
            >
              {
                MODE_OPTIONS.find(
                  mode => mode.id === modeFromSettings(settings)
                ).label
              }
            </Badge>
          </div>
        )}
      </PageHeader>
      {user && !accessLoading && !canEdit && (
        <Alert variant="info" className="small">
          Read-only access. Only users in the Topicz organization can change
          Rate Shop settings, rules or re-rate orders.
          {userError && ' Your organization could not be verified.'}
        </Alert>
      )}
      <LoadError error={settingsData.error} />
      <LoadError error={runsData.error} />
      <Card className="mb-3">
        <Card.Body>
          {signedOut ? (
            signIn
          ) : runsData.loading ? (
            <Loading label="Loading run summary…" />
          ) : (
            <Row className="g-3 align-items-center">
              <Col lg={8}>
                <h6>
                  Last run ·{' '}
                  {latest ? dateTime(latest.startedAt) : 'No runs yet'}
                </h6>
                {latest ? (
                  <>
                    <p className="small text-muted mb-2">
                      {latest.mode} · {latest.scanned ?? 0} orders scanned
                    </p>
                    <Counts counts={latest.counts} />
                  </>
                ) : (
                  <p className="small text-muted mb-0">
                    Run outcomes will appear after the first sweep.
                  </p>
                )}
              </Col>
              <Col lg={4} className="text-lg-end">
                <div className="small text-muted">Projected savings today</div>
                <div className="fs-2 fw-bold text-success">
                  {money(savingsToday)}
                </div>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
      <Tabs
        id="rate-shop-tabs"
        activeKey={activeTab}
        onSelect={tab => {
          if (!tab || tab === activeTab) return;
          if (activeTab === 'settings' && settingsDirty) setPendingTab(tab);
          else navigateTab(tab);
        }}
        mountOnEnter
        unmountOnExit
        className="mb-3"
      >
        <Tab eventKey="rules" title="Rules">
          {signedOut ? (
            signIn
          ) : (
            <RulesTab
              data={rulesData}
              storesData={storesData}
              settings={settings}
              getDecision={decisionsData.getDecision}
              canEdit={canEdit}
            />
          )}
        </Tab>
        <Tab eventKey="log" title="Log">
          {signedOut ? (
            signIn
          ) : (
            <LogTab
              data={decisionsData}
              canEdit={canEdit}
              stores={storesData.stores}
            />
          )}
        </Tab>
        <Tab eventKey="runs" title="Runs">
          {signedOut ? signIn : <RunsTab data={runsData} />}
        </Tab>
        <Tab eventKey="settings" title="Settings">
          {signedOut ? (
            signIn
          ) : (
            <SettingsTab
              data={settingsData}
              storesData={storesData}
              latest={latest}
              canEdit={canEdit}
              onDirtyChange={setSettingsDirty}
            />
          )}
        </Tab>
      </Tabs>
      <ConfirmModal
        show={Boolean(pendingTab)}
        title="Discard unsaved settings?"
        onHide={() => setPendingTab(null)}
        onConfirm={() => {
          navigateTab(pendingTab);
          setPendingTab(null);
        }}
        confirmLabel="Discard and switch tab"
      >
        <p>
          Your settings changes have not been saved. Discard them and switch
          tabs?
        </p>
      </ConfirmModal>
    </>
  );
};

export default RateShop;
